
import React from 'react';
import { ArticleBody } from "@/components/ui/typography";
import ArticleElaborateFeature from './article-ai/ArticleElaborateFeature';

interface EnhancedArticleContentProps {
  content?: string;
  editorData?: any;
  blocks?: any[];
  authorId?: string;
}

const EnhancedArticleContent: React.FC<EnhancedArticleContentProps> = ({ 
  content, 
  editorData,
  blocks,
  authorId
}) => {
  // Function to render a block based on its type
  const renderBlock = (block: any, index: number) => {
    if (!block) return null;
    
    switch (block.type) {
      case 'paragraph':
        const paragraphText = block.data?.text || '';
        return (
          <div 
            key={`paragraph-${index}`} 
            className="mb-4 relative group"
            id={`section-${index}`}
          >
            <p
              className="relative transition-all duration-200 group-hover:bg-primary/5 group-hover:rounded-md group-hover:px-2 group-hover:-mx-2"
              dangerouslySetInnerHTML={{ __html: paragraphText }}
            />
            <ArticleElaborateFeature paragraphText={paragraphText} paragraphIndex={index} />
          </div>
        );
      
      case 'header':
        const HeaderTag = `h${block.data?.level || 2}` as keyof JSX.IntrinsicElements;
        const headerText = block.data?.text || '';
        return (
          <div
            key={`header-${index}`}
            className="mt-6 mb-4 relative group"
            id={`section-${index}`}
          >
            <HeaderTag 
              className="font-bold relative transition-all duration-200 group-hover:bg-primary/5 group-hover:rounded-md group-hover:px-2 group-hover:-mx-2"
              dangerouslySetInnerHTML={{ __html: headerText }}
            />
            <ArticleElaborateFeature paragraphText={headerText} paragraphIndex={index} />
          </div>
        );
      
      case 'image':
        return (
          <figure key={`image-${index}`} className="my-6" id={`section-${index}`}>
            <img 
              src={block.data?.file?.url || block.data?.url} 
              alt={block.data?.caption || 'Article image'} 
              className="w-full rounded-md"
            />
            {block.data?.caption && (
              <figcaption className="mt-2 text-center text-sm text-muted-foreground">
                {block.data.caption}
              </figcaption>
            )}
          </figure>
        );
      
      case 'list':
        const ListTag = block.data?.style === 'ordered' ? 'ol' : 'ul';
        return (
          <div key={`list-${index}`} id={`section-${index}`}>
            <ListTag className="mb-4 ml-6">
              {block.data?.items?.map((item: string, i: number) => (
                <li 
                  key={`list-item-${i}`}
                  className={ListTag === 'ul' ? 'list-disc' : 'list-decimal'}
                  dangerouslySetInnerHTML={{ __html: item || '' }}
                />
              ))}
            </ListTag>
          </div>
        );
      
      case 'quote':
        const quoteText = block.data?.text || '';
        return (
          <div key={`quote-${index}`} className="relative group" id={`section-${index}`}>
            <blockquote className="border-l-4 border-primary pl-4 my-4 transition-all duration-200 group-hover:bg-primary/5 group-hover:rounded-md">
              <p className="italic" dangerouslySetInnerHTML={{ __html: quoteText }} />
              {block.data?.caption && (
                <cite className="block text-sm text-muted-foreground mt-2">— {block.data.caption}</cite>
              )}
            </blockquote>
            <ArticleElaborateFeature paragraphText={quoteText} paragraphIndex={index} />
          </div>
        );
      
      default:
        return (
          <div key={`unknown-${index}`} className="text-sm text-muted-foreground bg-muted p-2 rounded my-2" id={`section-${index}`}>
            Unknown block type: {block.type}
          </div>
        );
    }
  };

  // Determine which content source to use (in priority order)
  const renderContent = () => {
    // If blocks array is provided, render each block
    if (blocks && blocks.length > 0) {
      return blocks.map((block, index) => renderBlock({
        type: block.type,
        data: block.content
      }, index));
    }
    
    // If editorData is provided with blocks, render those
    if (editorData && editorData.blocks && editorData.blocks.length > 0) {
      return editorData.blocks.map(renderBlock);
    }
    
    // Fallback to HTML string content
    if (content) {
      // In a real implementation, we would parse the HTML content and add elaboration features
      // For now, return it as is
      return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }
    
    // No content available
    return <p className="text-muted-foreground">No content available for this article.</p>;
  };

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-0 mb-16">
      <ArticleBody>
        {renderContent()}
      </ArticleBody>
    </article>
  );
};

export default EnhancedArticleContent;
